/**
 * Show List - Matrix View with Language Columns
 *
 * Multi-language admin UI:
 * - Displays shows with aggregated translation status
 * - Language columns show status icons for each translation
 * - Click language icon to edit that specific translation
 */
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Checkbox,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Tooltip,
  CircularProgress,
  useTheme,
  Chip,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  CircleSlash,
  Search,
  X,
  Minus,
  Check,
  AlertTriangle,
} from 'lucide-react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  logger,
  MediaConfig,
  type ShowListItem,
  type Language,
} from '@musedrops/shared';
import { ShowBulkActions } from '../components/ShowBulkActions';
import { ShowImage } from '../components/ShowImage';
import { supabase } from '../../../services/supabase';
import { PageToolbar } from '../../../shared/components/PageToolbar';
import { DebugBadge } from '../../../shared/components/DebugBadge';
import {
  listShowsWithTrans,
  getShowPublishStatus,
  LAST_PUBLISHED_LOOKBACK_DAYS,
  type ShowPublishStatus,
} from '../../../data/show';
import { LanguageApi } from '../../../services/languageApi';
import { configApi, type AppConfig } from '../../../services/configApi';
import { getShowPublishWarning } from '../../../utils/showScheduling';
import { COLORS } from '../../../theme/tokens';

// Translation status indicator for language cells
const TranslationStatusIcon = ({
  hasTranslation,
  isApplicable,
  onClick,
}: {
  hasTranslation: boolean;
  isApplicable: boolean;
  onClick?: () => void;
}) => {
  // Not applicable for this show (show has specific language that doesn't match)
  if (!isApplicable) {
    return (
      <Tooltip title="Not applicable for this show">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            opacity: 0.2,
          }}
        >
          <Minus size={16} />
        </Box>
      </Tooltip>
    );
  }

  // Has translation
  if (hasTranslation) {
    return (
      <Tooltip title="Translation exists - click to edit">
        <IconButton
          size="small"
          onClick={e => {
            e.stopPropagation();
            onClick?.();
          }}
          sx={{
            fontSize: '1rem',
            width: 32,
            height: 32,
            color: 'success.main',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Check size={16} />
        </IconButton>
      </Tooltip>
    );
  }

  // Missing translation (applicable but not created)
  return (
    <Tooltip title="Missing translation - click to create">
      <IconButton
        size="small"
        onClick={e => {
          e.stopPropagation();
          onClick?.();
        }}
        sx={{
          fontSize: '1rem',
          width: 32,
          height: 32,
          color: 'error.main',
          opacity: 0.6,
          '&:hover': {
            backgroundColor: 'action.hover',
            opacity: 1,
          },
        }}
      >
        <CircleSlash size={16} />
      </IconButton>
    </Tooltip>
  );
};

// Show cell with image, name, disabled chip, and genre
const ShowCell = ({ show }: { show: ShowListItem }) => {
  const imageFile = show.imageUpdatedAt ? MediaConfig.show.coverImage : null;

  return (
    <Link
      to={`/shows/${show.id}/edit`}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flexShrink: 0 }}>
          <ShowImage
            showSlug={show.slug}
            imageFile={imageFile}
            size={70}
            alt={show.slug}
            style={{ borderRadius: '4px' }}
            mode="fill"
          />
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              {show.slug}
              {show.lang && show.lang !== 'en' && (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 'normal' }}
                >
                  [{show.lang}]
                </Typography>
              )}
            </Typography>
            {!show.enabled && (
              <Chip
                label="disabled"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  backgroundColor: 'error.main',
                  color: 'error.contrastText',
                }}
              />
            )}
            <DebugBadge show={Boolean(show.usedDebug)} />
          </Box>
          {show.genreName && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {show.genreName}
            </Typography>
          )}
        </Box>
      </Box>
    </Link>
  );
};

// Stories count cell with link
const StoriesCountCell = ({ show }: { show: ShowListItem }) => {
  const storyCount = show.storyCount || 0;
  const text = storyCount === 1 ? '1 story' : `${storyCount} stories`;

  if (storyCount === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Link
      to={`/stories?filter=${encodeURIComponent(JSON.stringify({ showSlug: show.slug }))}`}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      style={{ textDecoration: 'none' }}
    >
      <Typography
        variant="body2"
        sx={{
          color: 'primary.main',
          '&:hover': { textDecoration: 'underline' },
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </Typography>
    </Link>
  );
};

// Format relative time
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Date of the show's most recent story (may be in the future for scheduled stories)
const LastStoryCell = ({ show }: { show: ShowListItem }) => {
  if (!show.lastStoryAt) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  const date = new Date(show.lastStoryAt);
  return (
    <Tooltip title={date.toLocaleString()}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ whiteSpace: 'nowrap' }}
      >
        {date.toLocaleDateString()}
      </Typography>
    </Tooltip>
  );
};

// Tooltip text for the "Last Published" cell (issue #787)
const getLastPublishedTooltip = (
  warning: ReturnType<typeof getShowPublishWarning>,
  lastPublishedAt: string | null
): string => {
  if (!warning.isViolation) {
    if (lastPublishedAt) return new Date(lastPublishedAt).toLocaleString();
    return `No published translation found in the last ${LAST_PUBLISHED_LOOKBACK_DAYS} days`;
  }
  if (warning.reason === 'stale') {
    return `No published translation found in the last ${LAST_PUBLISHED_LOOKBACK_DAYS} days - show may be silently blocked`;
  }
  const expected = warning.expectedNextPublishAt
    ? new Date(warning.expectedNextPublishAt).toLocaleString()
    : 'now';
  return `Overdue - expected to publish by ${expected}`;
};

// Last-published story translation per show, with schedule-violation warning (issue #787)
const LastPublishedCell = ({
  show,
  status,
  config,
}: {
  show: ShowListItem;
  status: ShowPublishStatus | undefined;
  config: AppConfig | null;
}) => {
  const storyCount = show.storyCount || 0;

  if (storyCount === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  // Still loading the batched publish-status/config fetch for this page of shows.
  if (!status || !config) {
    return (
      <Typography variant="body2" color="text.secondary">
        …
      </Typography>
    );
  }

  const warning = getShowPublishWarning(
    {
      slug: show.slug,
      enabled: show.enabled,
      storyCount,
      customPublishWeekdays: status.customPublishWeekdays,
    },
    status.lastPublishedAt,
    config
  );

  const dateLabel = status.lastPublishedAt
    ? formatRelativeTime(status.lastPublishedAt)
    : `No publish in ${LAST_PUBLISHED_LOOKBACK_DAYS}d+`;

  const tooltipTitle = getLastPublishedTooltip(warning, status.lastPublishedAt);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography
        variant="body2"
        color={warning.isViolation ? 'error.main' : 'text.secondary'}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {dateLabel}
      </Typography>
      {warning.isViolation && (
        <Tooltip title={tooltipTitle}>
          <Box sx={{ display: 'flex', color: 'error.main' }}>
            <AlertTriangle size={14} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
type FilterChangeHandler = (value: string) => void;

interface ShowListActionsProps {
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  genreFilter: string;
  onGenreChange: FilterChangeHandler;
  enabledFilter: string;
  onEnabledChange: FilterChangeHandler;
  searchQuery: string;
  onSearchQueryChange: FilterChangeHandler;
  onSearchSubmit: () => void;
  onClearSearch: () => void;
  genres: Array<{ id: string; name: string }>;
}

const ShowListActions = ({
  onClearFilters,
  hasActiveFilters,
  genreFilter,
  onGenreChange,
  enabledFilter,
  onEnabledChange,
  searchQuery,
  onSearchQueryChange,
  onSearchSubmit,
  onClearSearch,
  genres,
}: ShowListActionsProps) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const activeColor =
    theme.palette.mode === 'dark'
      ? COLORS.activeFilterBackgroundDark
      : COLORS.activeFilterBackgroundLight;

  const handleAddShow = () => {
    navigate('/shows/create');
  };

  return (
    <PageToolbar
      left={
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Genre</InputLabel>
            <Select
              value={genreFilter}
              onChange={e => onGenreChange(e.target.value)}
              label="Genre"
              sx={{
                backgroundColor: genreFilter ? activeColor : 'transparent',
              }}
            >
              <MenuItem value="">All Genres</MenuItem>
              {genres.map(genre => (
                <MenuItem key={genre.id} value={genre.id}>
                  {genre.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={enabledFilter}
              onChange={e => onEnabledChange(e.target.value)}
              label="Status"
              sx={{
                backgroundColor: enabledFilter ? activeColor : 'transparent',
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Enabled</MenuItem>
              <MenuItem value="false">Disabled</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search..."
            value={searchQuery}
            onChange={e => onSearchQueryChange(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onSearchSubmit()}
            sx={{
              minWidth: 180,
              '& .MuiOutlinedInput-root': {
                backgroundColor: searchQuery ? activeColor : 'transparent',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={onClearSearch}
                    edge="end"
                    sx={{ mr: -1 }}
                  >
                    <X size={18} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {hasActiveFilters && (
            <Button variant="outlined" size="small" onClick={onClearFilters}>
              Clear Filters
            </Button>
          )}
        </Box>
      }
      right={
        <Button variant="contained" onClick={handleAddShow}>
          Add Show
        </Button>
      }
    />
  );
};

const EmptyState = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '50vh',
        width: '100%',
        textAlign: 'center',
      }}
    >
      <Typography variant="h5" gutterBottom>
        No shows yet
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Create your first show to get started
      </Typography>
      <Button variant="contained" onClick={() => navigate('/shows/create')}>
        Create Your First Show
      </Button>
    </Box>
  );
};

// Parse filter from URL
const parseFilterFromUrl = (
  searchParams: URLSearchParams
): { genreId?: string; enabled?: string; q?: string } => {
  const filterJson = searchParams.get('filter');
  if (filterJson) {
    try {
      return JSON.parse(filterJson);
    } catch {
      // Ignore parse errors
    }
  }

  return {
    genreId: searchParams.get('genreId') || undefined,
    enabled: searchParams.get('enabled') || undefined,
    q: searchParams.get('q') || undefined,
  };
};

// Sort configuration types
type SortField = 'slug' | 'storyCount' | 'lastStoryAt' | 'updatedAt' | 'languages';
type SortOrder = 'asc' | 'desc';

// Fields that require client-side sorting (not in DB view as sortable columns)
const CLIENT_SORT_FIELDS = new Set<SortField>(['languages']);

export const ShowList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [shows, setShows] = useState<ShowListItem[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [genres, setGenres] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  // Issue #787: Last-published / schedule-violation column state
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const [publishStatusBySlug, setPublishStatusBySlug] = useState<
    Record<string, ShowPublishStatus>
  >({});

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Sorting (default: updated desc)
  const [sortField, setSortField] = useState<SortField>('updatedAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Applied filters from URL
  const appliedFilters = useMemo(
    () => parseFilterFromUrl(searchParams),
    [searchParams]
  );

  // Local filter state
  const [genreFilter, setGenreFilter] = useState<string>(
    appliedFilters.genreId || ''
  );
  const [enabledFilter, setEnabledFilter] = useState<string>(
    appliedFilters.enabled || ''
  );
  const [searchQuery, setSearchQuery] = useState<string>(
    appliedFilters.q || ''
  );

  const isUpdatingUrl = useRef(false);

  // Sync local state from URL
  useEffect(() => {
    if (isUpdatingUrl.current) {
      isUpdatingUrl.current = false;
      return;
    }
    setGenreFilter(appliedFilters.genreId || '');
    setEnabledFilter(appliedFilters.enabled || '');
    setSearchQuery(appliedFilters.q || '');
  }, [appliedFilters]);

  const hasActiveFilters = Boolean(
    appliedFilters.genreId || appliedFilters.enabled || appliedFilters.q
  );

  // Update URL
  const applyFilters = useCallback(
    (newGenreId: string, newEnabled: string, newSearchQuery: string) => {
      isUpdatingUrl.current = true;
      const params: Record<string, string> = {};
      if (newGenreId) params.genreId = newGenreId;
      if (newEnabled) params.enabled = newEnabled;
      if (newSearchQuery) params.q = newSearchQuery;
      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  // Filter changes apply immediately
  const handleGenreChange = useCallback(
    (value: string) => {
      setGenreFilter(value);
      applyFilters(value, enabledFilter, appliedFilters.q || '');
    },
    [enabledFilter, appliedFilters.q, applyFilters]
  );

  const handleEnabledChange = useCallback(
    (value: string) => {
      setEnabledFilter(value);
      applyFilters(genreFilter, value, appliedFilters.q || '');
    },
    [genreFilter, appliedFilters.q, applyFilters]
  );

  const handleSearchQueryChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleSearchSubmit = useCallback(() => {
    applyFilters(genreFilter, enabledFilter, searchQuery);
  }, [genreFilter, enabledFilter, searchQuery, applyFilters]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    applyFilters(genreFilter, enabledFilter, '');
  }, [genreFilter, enabledFilter, applyFilters]);

  // Global languages for column headers
  const globalLanguages = useMemo(() => {
    return languages.filter(lang => lang.includeInGlobalGen);
  }, [languages]);

  // Map of show ID → slug for bulk actions
  const showSlugs = useMemo(() => {
    const map: Record<string, string> = {};
    for (const show of shows) {
      map[show.id] = show.slug;
    }
    return map;
  }, [shows]);

  // Load languages
  useEffect(() => {
    const loadLanguages = async () => {
      try {
        const langs = await LanguageApi.getAll();
        setLanguages(langs);
      } catch (error) {
        logger.error('Failed to load languages', { error });
      }
    };
    loadLanguages();
  }, []);

  // Load genres
  useEffect(() => {
    const loadGenres = async () => {
      const { data, error } = await supabase
        .from('genres')
        .select('id, name')
        .order('name');
      if (!error && data) {
        setGenres(data);
      }
    };
    loadGenres();
  }, []);

  // Issue #787: Load global publish-schedule config once (default weekdays/hour)
  useEffect(() => {
    const loadAppConfig = async () => {
      try {
        const { config } = await configApi.getAppConfig();
        setAppConfig(config);
      } catch (error) {
        logger.error('Failed to load app config for publish-schedule check', { error });
      }
    };
    loadAppConfig();
  }, []);

  // Issue #787: Batched fetch of last-published-translation + schedule override
  // data for the shows currently visible on this page (ONE query per concern,
  // not per-row - see getShowPublishStatus in data/show.ts).
  useEffect(() => {
    const loadPublishStatus = async () => {
      if (shows.length === 0) {
        setPublishStatusBySlug({});
        return;
      }
      try {
        const slugs = shows.map(show => show.slug);
        const status = await getShowPublishStatus(slugs);
        setPublishStatusBySlug(status);
      } catch (error) {
        logger.error('Failed to load show publish status', { error });
      }
    };
    loadPublishStatus();
  }, [shows]);

  // Helper to count translations
  const countTranslations = useCallback(
    (show: ShowListItem) => {
      if (!show.translations) return 0;
      return Object.keys(show.translations).length;
    },
    []
  );

  // Load shows
  const loadShows = useCallback(async () => {
    setLoading(true);
    try {
      const filter: Record<string, unknown> = {};
      if (appliedFilters.genreId) filter.genreId = appliedFilters.genreId;
      if (appliedFilters.enabled) filter.enabled = appliedFilters.enabled === 'true';
      if (appliedFilters.q) filter.q = appliedFilters.q;

      // For client-side sort fields, we fetch with default DB sort and sort client-side
      const isClientSort = CLIENT_SORT_FIELDS.has(sortField);
      const dbSortField = isClientSort ? 'updatedAt' : sortField;
      const dbSortOrder = isClientSort ? 'DESC' : sortOrder.toUpperCase() as 'ASC' | 'DESC';

      const result = await listShowsWithTrans({
        page: page + 1,
        perPage: rowsPerPage,
        filter,
        withTotal: true,
        sortField: dbSortField,
        sortOrder: dbSortOrder,
      });

      const { data: resultData, total } = result;
      let data = resultData;

      // Client-side sort for languages (translations count)
      if (isClientSort) {
        data = [...data].sort((a, b) => {
          const aCount = countTranslations(a);
          const bCount = countTranslations(b);
          return sortOrder === 'asc' ? aCount - bCount : bCount - aCount;
        });
      }

      setShows(data);
      setTotal(total ?? resultData.length);
    } catch (error) {
      logger.error('Failed to load shows', { error });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, appliedFilters, sortField, sortOrder, countTranslations]);

  useEffect(() => {
    loadShows();
  }, [loadShows]);

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel('shows-list')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'shows' },
        () => {
          setTimeout(() => loadShows(), 1000);
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'show_translations' },
        () => {
          setTimeout(() => loadShows(), 1000);
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
      supabase.removeChannel(channel);
    };
  }, [loadShows]);

  // Visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadShows();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadShows]);

  // Handlers
  const handleRowClick = (e: React.MouseEvent, showId: string) => {
    const url = `/shows/${showId}/edit`;
    if (e.metaKey || e.ctrlKey) {
      window.open(url, '_blank');
      return;
    }
    navigate(url);
  };

  const handleLanguageClick = (showId: string, lang: string) => {
    navigate(`/shows/${showId}/edit?tab=languages&lang=${lang}`);
  };

  const handleSelectAll = (checked: boolean) => {
    setSelected(checked ? shows.map(s => s.id) : []);
  };

  const handleSelectOne = (showId: string, checked: boolean) => {
    setSelected(prev =>
      checked ? [...prev, showId] : prev.filter(id => id !== showId)
    );
  };

  const handleClearFilters = useCallback(() => {
    setGenreFilter('');
    setEnabledFilter('');
    setSearchQuery('');
    isUpdatingUrl.current = true;
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sort handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle order if same field
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      // New field, default to desc
      setSortField(field);
      setSortOrder('desc');
    }
    setPage(0); // Reset to first page on sort change
  };

  if (loading && shows.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!loading && shows.length === 0 && !hasActiveFilters) {
    return <EmptyState />;
  }

  return (
    <Box>
      <ShowListActions
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
        genreFilter={genreFilter}
        onGenreChange={handleGenreChange}
        enabledFilter={enabledFilter}
        onEnabledChange={handleEnabledChange}
        searchQuery={searchQuery}
        onSearchQueryChange={handleSearchQueryChange}
        onSearchSubmit={handleSearchSubmit}
        onClearSearch={handleClearSearch}
        genres={genres}
      />

      {/* Bulk Action Bar */}
      {selected.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            mb: 1,
            backgroundColor: 'action.selected',
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
            {selected.length} {selected.length === 1 ? 'show' : 'shows'} selected
          </Typography>
          <ShowBulkActions
            selectedIds={selected}
            showSlugs={showSlugs}
            onComplete={() => setSelected([])}
            onNotification={(message, type) => setNotification({ message, type })}
          />
        </Box>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small" sx={{ tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 'auto' }}>
                <Checkbox
                  indeterminate={
                    selected.length > 0 && selected.length < shows.length
                  }
                  checked={shows.length > 0 && selected.length === shows.length}
                  onChange={e => handleSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell sx={{ width: 'auto' }}>
                <TableSortLabel
                  active={sortField === 'slug'}
                  direction={sortField === 'slug' ? sortOrder : 'desc'}
                  onClick={() => handleSort('slug')}
                >
                  Show
                </TableSortLabel>
              </TableCell>
              {globalLanguages.map((lang, index) => (
                <TableCell
                  key={lang.key}
                  align="center"
                  sx={{ width: 50, minWidth: 50 }}
                >
                  {index === 0 ? (
                    <Tooltip title={`Sort by translation count (${lang.name})`}>
                      <TableSortLabel
                        active={sortField === 'languages'}
                        direction={sortField === 'languages' ? sortOrder : 'desc'}
                        onClick={() => handleSort('languages')}
                      >
                        {lang.key}
                      </TableSortLabel>
                    </Tooltip>
                  ) : (
                    <Tooltip title={lang.name}>
                      <span>{lang.key}</span>
                    </Tooltip>
                  )}
                </TableCell>
              ))}
              <TableCell sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                <TableSortLabel
                  active={sortField === 'storyCount'}
                  direction={sortField === 'storyCount' ? sortOrder : 'desc'}
                  onClick={() => handleSort('storyCount')}
                >
                  Stories
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                <TableSortLabel
                  active={sortField === 'lastStoryAt'}
                  direction={sortField === 'lastStoryAt' ? sortOrder : 'desc'}
                  onClick={() => handleSort('lastStoryAt')}
                >
                  Last Story
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                Last Published
              </TableCell>
              <TableCell sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                <TableSortLabel
                  active={sortField === 'updatedAt'}
                  direction={sortField === 'updatedAt' ? sortOrder : 'desc'}
                  onClick={() => handleSort('updatedAt')}
                >
                  Updated
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shows.map(show => {
              const isSelected = selected.includes(show.id);
              // Determine which languages apply to this show
              const applicableLangs = show.lang
                ? [show.lang] // Single-language show
                : globalLanguages.map(l => l.key); // All languages

              return (
                <TableRow
                  key={show.id}
                  hover
                  selected={isSelected}
                  onClick={(e) => handleRowClick(e, show.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell
                    padding="checkbox"
                    onClick={e => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={e => handleSelectOne(show.id, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>
                    <ShowCell show={show} />
                  </TableCell>
                  {globalLanguages.map(lang => {
                    const isApplicable = applicableLangs.includes(lang.key);
                    const hasTranslation = Boolean(show.translations?.[lang.key]);

                    return (
                      <TableCell key={lang.key} align="center">
                        <TranslationStatusIcon
                          hasTranslation={hasTranslation}
                          isApplicable={isApplicable}
                          onClick={() => handleLanguageClick(show.id, lang.key)}
                        />
                      </TableCell>
                    );
                  })}
                  <TableCell>
                    <StoriesCountCell show={show} />
                  </TableCell>
                  <TableCell>
                    <LastStoryCell show={show} />
                  </TableCell>
                  <TableCell>
                    <LastPublishedCell
                      show={show}
                      status={publishStatusBySlug[show.slug]}
                      config={appConfig}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatRelativeTime(show.updatedAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification(null)}
          severity={notification?.type}
          variant="filled"
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
